import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { fetchSelectOrgOptions } from '../api/auth'
import BrandLogo from '../components/BrandLogo'
import { useAuth } from '../hooks/useAuth'
import type { AuthOrgOption } from '../types/api'

/**
 * The org chooser, on its own route.
 *
 * It lives here rather than inside LoginPage so it cannot render on the sign-in
 * screen by mistake. Choosing an org is strictly a POST-authentication step:
 * you arrive holding a select token, which is only ever issued after a password
 * matched or a provider verified you. There is no org selection before login.
 *
 * Both paths land here with nothing but that token:
 *   - password  → LoginPage navigates with it in router state
 *   - OAuth     → the callback redirects with ?selectToken=
 *
 * The org list is fetched from the server against the token and contains only
 * the orgs this identity can actually enter — never the deployment's roster.
 */
export default function SelectOrgPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { selectOrg } = useAuth()

  const stateToken = (location.state as { selectToken?: string } | null)?.selectToken
  const selectToken = stateToken ?? searchParams.get('selectToken') ?? null

  const [orgs, setOrgs] = useState<AuthOrgOption[] | null>(null)
  const [selectedSlug, setSelectedSlug] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!selectToken) {
      navigate('/login', { replace: true })
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const options = await fetchSelectOrgOptions(selectToken)
        if (cancelled) return
        setOrgs(options)
        if (options.length === 1 && options[0]) setSelectedSlug(options[0].slug)
      } catch {
        if (cancelled) return
        // Expired or tampered token — send them back to sign in rather than
        // stranding them on an empty chooser.
        setLoadError('This sign-in attempt has expired. Please sign in again.')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [selectToken, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectToken || !selectedSlug || isSubmitting) return
    setIsSubmitting(true)
    try {
      // selectOrg exchanges the token for full tokens and hard-navigates to
      // /{slug}/dashboard (see useAuth).
      await selectOrg(selectToken, selectedSlug)
      toast.success('Welcome back!')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Organization selection failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen app-sky flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo size={64} src="/icon-256.png" className="mx-auto rounded-2xl mb-4" />
          <h1 className="text-2xl font-bold text-harbor-text">Choose your organization</h1>
          <p className="text-harbor-text-secondary mt-2">
            Your account belongs to more than one organization
          </p>
        </div>

        {loadError ? (
          <p className="text-center text-sm text-harbor-text-secondary">{loadError}</p>
        ) : orgs === null ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="org-select"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Organization
              </label>
              <select
                id="org-select"
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="" disabled>
                  Select an organization…
                </option>
                {orgs.map((org) => (
                  <option key={org.slug} value={org.slug}>
                    {org.name} ({org.slug})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting || !selectedSlug}
              className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Signing in…' : 'Continue'}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 w-full text-center text-sm text-harbor-text-secondary hover:text-harbor-text"
        >
          Back to sign in
        </button>
      </div>
    </div>
  )
}
